/*
 * recharge-cart-pp.js
 * ---------------------------------------------------------------------------
 * Order Protection inside the Recharge Storefront Experiences cart drawer.
 *
 * The Recharge cart drawer renders in a shadow root and treats the hidden
 * "Order Protection" product as a normal line item (image, qty stepper, trash).
 * This script mirrors what custom-pp.v2.js does for the native drawer:
 *   1. hides the Order Protection line item in the Recharge drawer
 *   2. renders an on/off toggle in the drawer footer, under the subtotal
 *   3. keeps the correct price-tier variant in the Shopify cart, reacting to
 *      Recharge's own cart events
 *
 * No-ops unless the Recharge cart drawer experience is enabled on the page,
 * so it is inert on the live theme (embed off) and on funnel templates.
 * Runs alongside custom-pp.v2.js; both write the same idempotent payload.
 */
(function () {
  "use strict";

  var RC = window.RechargeStorefrontConfig;
  if (!RC || !RC.cart_drawer || !RC.cart_drawer.enabled) return;

  var HOST_ID = "recharge-storefront-experiences__cart-drawer";
  var OP_TITLE = "Order Protection";

  // Same variants / tiers / thresholds as custom-pp.v2.js
  var TIER_VARIANTS = {
    1: 46069180596442,
    2: 46069180629210,
    3: 46069180661978,
    4: 46069180694746,
  };
  var TIER_PRICES = { 1: "2.34", 2: "3.60", 3: "5.40", 4: "7.20" };
  var OP_IDS = Object.keys(TIER_VARIANTS).map(function (k) {
    return TIER_VARIANTS[k];
  });
  var OP_ID_SET = new Set(OP_IDS);

  var TBYB = /tbyb|try-before-you-buy/i.test(window.location.href);

  var ppChecked = true; // survives drawer re-renders (DOM checkbox is rebuilt)
  var ppBusy = false; // re-entrancy guard for our own /cart/update.js writes

  // --- tier maths (identical rules to custom-pp.v2.js) ---
  function tierFor(cents) {
    var t = cents / 100;
    if (t >= 200) return 4;
    if (t >= 150) return 3;
    if (t >= 100) return 2;
    if (t >= 3) return 1;
    return null;
  }

  function nonOpTotal(cart) {
    return (cart.items || []).reduce(function (sum, item) {
      if (OP_ID_SET.has(item.id)) return sum;
      return sum + (item.final_line_price || item.line_price || 0);
    }, 0);
  }

  function shadow() {
    var host = document.getElementById(HOST_ID);
    return host && host.shadowRoot ? host.shadowRoot : null;
  }

  // --- styles, injected once directly on the shadow root (survives close) ---
  var STYLE_ID = "mmh-pp-style";
  var CSS = [
    "[data-mmh-pp-hidden]{display:none!important}",
    ".mmh-pp{width:100%;box-sizing:border-box;padding:12px 24px 7px;margin-top:10px;border-top:1px solid rgba(0,0,0,.08)}",
    ".mmh-pp__inner{display:flex;align-items:flex-start;gap:16px;width:100%;justify-content:space-between}",
    ".mmh-pp__content{display:flex;flex-direction:column}",
    ".mmh-pp__title{margin:0 0 4px;font-weight:500;font-size:15px;line-height:1.1}",
    ".mmh-pp__subtitle{margin:0;font-weight:300;font-size:11px;line-height:1.1;opacity:.7}",
    ".mmh-pp__cost-wrap{display:flex;align-items:center;gap:10px}",
    ".mmh-pp__cost{margin:0;font-weight:500;font-size:14px;line-height:1;white-space:nowrap}",
    ".mmh-pp__toggle{margin:0;height:22px;width:36px;position:relative;cursor:pointer;flex:0 0 auto;user-select:none}",
    ".mmh-pp__toggle input{position:absolute;opacity:0;height:0;width:0;margin:0}",
    ".mmh-pp__track{position:absolute;inset:0;border-radius:15px;background:#aeb0b5;transition:background-color .15s ease-in}",
    ".mmh-pp__toggle input:checked~.mmh-pp__track{background:#333}",
    '.mmh-pp__track::after{content:"";position:absolute;left:2px;top:2px;width:18px;height:18px;border-radius:50%;background:#fff;transition:.15s ease-in}',
    ".mmh-pp__toggle input:checked~.mmh-pp__track::after{left:16px}",
  ].join("");

  function ensureStyle(sr) {
    if (sr.getElementById(STYLE_ID)) return;
    var s = document.createElement("style");
    s.id = STYLE_ID;
    s.textContent = CSS;
    sr.appendChild(s);
  }

  function isOpLineItem(li) {
    if (li.querySelector('img[alt="' + OP_TITLE + '"]')) return true;
    var p = li.querySelector("p.font-semibold") || li.querySelector("p");
    return !!p && p.textContent.trim() === OP_TITLE;
  }

  function hideOpLineItem(sr) {
    var items = sr.querySelectorAll(
      '#cart-drawer section[role="list"] > div[role="listitem"]',
    );
    for (var i = 0; i < items.length; i++) {
      var li = items[i];
      if (li.hasAttribute("data-mmh-pp-hidden")) continue;
      if (isOpLineItem(li)) li.setAttribute("data-mmh-pp-hidden", "");
    }
  }

  function buildToggle() {
    var wrap = document.createElement("div");
    wrap.className = "mmh-pp";
    wrap.id = "mmh-pp";
    wrap.innerHTML =
      '<div class="mmh-pp__inner">' +
      '<div class="mmh-pp__content">' +
      '<p class="mmh-pp__title">Order Protection</p>' +
      '<p class="mmh-pp__subtitle">Protection for Damage, Loss, Theft &amp; More!</p>' +
      "</div>" +
      '<div class="mmh-pp__cost-wrap">' +
      '<span class="mmh-pp__cost"></span>' +
      '<label class="mmh-pp__toggle">' +
      '<input type="checkbox"' +
      (ppChecked ? " checked" : "") +
      ">" +
      '<span class="mmh-pp__track"></span>' +
      "</label>" +
      "</div>" +
      "</div>";
    wrap.querySelector("input").addEventListener("change", function () {
      ppChecked = this.checked;
      sync();
    });
    return wrap;
  }

  // Returns true when it just created the toggle (caller should then sync()).
  function ensureToggle(sr) {
    if (TBYB) return false;
    if (sr.getElementById("mmh-pp")) return false;
    var footer = sr.querySelector("#cart-drawer footer");
    if (!footer) return false;
    var node = buildToggle();
    var subtotalRow = footer.firstElementChild;
    if (subtotalRow && subtotalRow.nextSibling) {
      footer.insertBefore(node, subtotalRow.nextSibling);
    } else {
      footer.appendChild(node);
    }
    return true;
  }

  function setCostLabel(sr, text) {
    var el = sr && sr.getElementById("mmh-pp");
    if (!el) return;
    var cost = el.querySelector(".mmh-pp__cost");
    if (cost) cost.textContent = text;
  }

  // Keep the drawer DOM in shape. Returns true if the toggle was (re)created.
  function paint() {
    var sr = shadow();
    if (!sr) return false;
    ensureStyle(sr);
    if (!sr.querySelector("#cart-drawer")) return false; // drawer closed
    hideOpLineItem(sr);
    return ensureToggle(sr);
  }

  // Reconcile the Order Protection variant in the Shopify cart.
  async function sync(cartFromEvent) {
    var created = paint();

    if (ppBusy) return;

    var sr = shadow();
    var cart =
      cartFromEvent ||
      (await fetch("/cart.js").then(function (r) {
        return r.json();
      }));

    var desiredTier = ppChecked && !TBYB ? tierFor(nonOpTotal(cart)) : null;
    var desiredVariant = desiredTier ? TIER_VARIANTS[desiredTier] : null;

    setCostLabel(sr, desiredTier ? "$" + TIER_PRICES[desiredTier] : "$0.00");

    var inCart = (cart.items || []).filter(function (i) {
      return OP_ID_SET.has(i.id);
    });
    var alreadyCorrect =
      inCart.length === 1 &&
      inCart[0].id === desiredVariant &&
      inCart[0].quantity === 1;

    if (alreadyCorrect) return;
    if (!desiredVariant && inCart.length === 0) return;

    ppBusy = true;
    var updates = {};
    OP_IDS.forEach(function (v) {
      updates[v] = 0;
    });
    if (desiredVariant) updates[desiredVariant] = 1;

    try {
      await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates: updates }),
      });
      // Recharge auto-detects the /cart write and refreshes its drawer.
    } catch (e) {
      /* leave the cart as-is on failure */
    } finally {
      setTimeout(function () {
        ppBusy = false;
      }, 700);
      // if we just built the toggle, its label was set above; no-op otherwise
      if (created) setTimeout(function () { sync(); }, 250);
    }
  }

  var lastEventCart = null;
  function onCartEvent(payload) {
    lastEventCart = payload && payload.cart ? payload.cart : null;
    // let the drawer finish its own re-render first
    setTimeout(function () {
      sync(lastEventCart);
    }, 60);
  }

  function boot() {
    var pubsubBound = false;
    var rafPending = false;

    function schedulePaint() {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(function () {
        rafPending = false;
        if (paint()) sync();
      });
    }

    // RechargePubSub and the shadow host are both created asynchronously by the
    // Recharge module script, which can land after this deferred script runs —
    // so keep polling until each is available.
    var tries = 0;
    var iv = setInterval(function () {
      if (!pubsubBound && window.RechargePubSub) {
        pubsubBound = true;
        window.RechargePubSub.subscribe("cart:update", onCartEvent);
        window.RechargePubSub.subscribe("cart:refresh", onCartEvent);
        sync();
      }

      var host = document.getElementById(HOST_ID);
      if (host && host.shadowRoot && !host.__mmhObserved) {
        host.__mmhObserved = true;
        new MutationObserver(schedulePaint).observe(host.shadowRoot, {
          childList: true,
          subtree: true,
        });
      }

      if (paint()) sync();

      // stop polling once everything is wired; the observer + events take over
      if ((pubsubBound && host && host.__mmhObserved) || ++tries > 40) {
        clearInterval(iv);
      }
    }, 250);

    sync();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
